/** Поиск отменяет устаревшие запросы; офлайн выдача явно ограничена репликой. */
import { useEffect, useRef, useState } from 'react'
import { api, unwrap } from '@shared/api/client'
import type { Message, paths } from '@shared/api/types'
import { conversationsRepo, messagesRepo } from '@shared/db'

type Filters = NonNullable<paths['/messages/search']['get']['parameters']['query']>

export function useMessageSearch(filters: Filters) {
  const [items, setItems] = useState<Message[]>([])
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const [localOnly, setLocalOnly] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const nextRef = useRef<() => void>(() => undefined)
  const key = JSON.stringify(filters)
  useEffect(() => {
    const query = JSON.parse(key) as Filters
    const controller = new AbortController()
    let cursor: string | undefined
    let busy = false
    let first = true
    setItems([])
    setHasMore(false)
    setFailed(false)
    setLocalOnly(false)
    setLoading(false)
    if (!(query.q?.trim() || query.type || query.has_link || query.sender_id || query.from || query.to))
      return
    setLoading(true)
    const load = async () => {
      if (busy || controller.signal.aborted) return
      busy = true
      setLoading(true)
      setFailed(false)
      try {
        if (!navigator.onLine) throw new Error('offline')
        const result = unwrap(
          await api.GET('/messages/search', {
            params: { query: { ...query, limit: 30, ...(cursor ? { cursor } : {}) } },
            signal: controller.signal,
          }),
        )
        if (controller.signal.aborted) return
        setItems((old) => [
          ...new Map([...(first ? [] : old), ...result.items].map((m) => [m.id, m])).values(),
        ])
        first = false
        cursor = result.next_cursor ?? undefined
        setHasMore(Boolean(cursor))
        setLocalOnly(false)
      } catch {
        if (controller.signal.aborted) return
        setFailed(navigator.onLine)
        setLocalOnly(true)
        if (first) {
          const conversations = await conversationsRepo.list('all')
          const all = (
            await Promise.all(
              conversations
                .filter((c) => !query.conversation_id || c.id === query.conversation_id)
                .map((c) => messagesRepo.history(c.id)),
            )
          ).flat()
          if (controller.signal.aborted) return
          const needle = query.q?.toLocaleLowerCase() ?? ''
          setItems(
            all
              .filter(
                (m) =>
                  !m.deleted &&
                  (m.text ?? '').toLocaleLowerCase().includes(needle) &&
                  (!query.type || m.type === query.type) &&
                  (!query.sender_id || m.sender_id === query.sender_id) &&
                  (!query.has_link || /https?:\/\//i.test(m.text ?? '')) &&
                  (!query.from || m.created_at >= query.from) &&
                  (!query.to || m.created_at <= query.to),
              )
              .sort((a, b) => b.created_at.localeCompare(a.created_at)),
          )
        }
      } finally {
        busy = false
        if (!controller.signal.aborted) setLoading(false)
      }
    }
    nextRef.current = () => void load()
    const timer = setTimeout(() => void load(), 250)
    const online = () => void load()
    window.addEventListener('online', online)
    return () => {
      clearTimeout(timer)
      controller.abort()
      window.removeEventListener('online', online)
    }
  }, [key])
  return { items, loading, failed, localOnly, hasMore, loadMore: () => nextRef.current() }
}
